import { getToken } from '@luminovo/auth';
import { groupBy } from '@luminovo/commons';
import {
    http,
    QuoteRequestDTO,
    QuoteRequestField,
    QuoteRequestLineItemDTO,
    SupplierAndStockLocationDTO,
    SupplierPortalColumnSettings,
    SupplierPortalSettingsDTO,
} from '@luminovo/http-client';
import { formatSupplierAndStockLocationDTO } from '@luminovo/sourcing-core';
import { useSuspenseQuery, UseSuspenseQueryResult } from '@tanstack/react-query';
import { FormFieldConfiguration, FormFieldsConfiguration } from '../../../types';
import { resolveOtsPartsRecursively } from '../model';
import { QuoteRequest } from '../types';

export type QuoteRequestWithQuoteTracking = QuoteRequest & { quoteRequest: QuoteRequestDTO };

export function useSuspenseQuoteImporterState(props: {
    quoteRequestId: string;
    supplierPortalSettings?: boolean;
    defaultFormFieldsConfiguration: FormFieldsConfiguration;
}): UseSuspenseQueryResult<{
    quoteRequests: QuoteRequestWithQuoteTracking[];
    defaultQuoteRequest: QuoteRequestWithQuoteTracking | undefined;
    initialFieldConfiguration: FormFieldsConfiguration;
}> {
    const defaultFormFieldsConfiguration = props.defaultFormFieldsConfiguration;

    return useSuspenseQuery({
        queryKey: ['quote-requests', props.quoteRequestId, props.supplierPortalSettings],
        queryFn: async (): Promise<{
            quoteRequests: QuoteRequestWithQuoteTracking[];
            supplierPortalSettings: SupplierPortalSettingsDTO | undefined;
        }> => {
            const suppliersPromise: Promise<SupplierAndStockLocationDTO[]> = http(
                'GET /suppliers-and-stock-locations',
                {},
                getToken(),
            ).then(({ data }: { data: SupplierAndStockLocationDTO[] }) => data);

            const supplierPortalSettingsPromise: Promise<SupplierPortalSettingsDTO | undefined> =
                fetchSupplierPortalSettings(props.supplierPortalSettings ?? false);

            const quoteRequestPromise: Promise<QuoteRequestDTO> = http(
                'GET /quote-request/:id',
                {
                    pathParams: { id: props.quoteRequestId },
                },
                getToken(),
            ).then((data: QuoteRequestDTO) => data);

            const quoteRequestLineItemsPromise: Promise<QuoteRequestLineItemDTO[]> = http(
                'GET /quote-request/:id/line-items',
                {
                    pathParams: { id: props.quoteRequestId },
                },
                getToken(),
            ).then((data) => data.items);

            const [supplierAndStockLocations, quoteRequest, quoteRequestLineItems] = await Promise.all([
                suppliersPromise,
                quoteRequestPromise,
                quoteRequestLineItemsPromise,
            ]);

            const requestedParts = quoteRequestLineItems.flatMap((li) => resolveOtsPartsRecursively(li.requested_part));

            const { items: resolvedParts } = await http(
                'POST /parts/off-the-shelf/bulk',
                {
                    requestBody: {
                        ids: requestedParts.map((p) => p.id),
                        rfq_context: 'OutsideRfQ',
                    },
                },
                getToken(),
            );

            const supplierAndStockLocatationsBySupplierId = groupBy(supplierAndStockLocations, (s) => s.supplier.id);

            const sasls = supplierAndStockLocatationsBySupplierId[quoteRequest.supplier.id] ?? [];

            const quoteRequestsWithSupplier = sasls.map((sasl) => ({
                quoteRequest,
                supplierAndStockLocation: sasl,
            }));

            const quoteRequests: QuoteRequestWithQuoteTracking[] = quoteRequestsWithSupplier.map(
                ({ quoteRequest, supplierAndStockLocation }): QuoteRequestWithQuoteTracking => {
                    return {
                        id: quoteRequest.id,
                        label: `${formatSupplierAndStockLocationDTO(supplierAndStockLocation)} #${quoteRequest.number}`,
                        supplierAndStockLocation: supplierAndStockLocation.id,
                        status: quoteRequest.status,
                        resolvedParts,
                        quoteRequest,
                        disableFetchingQuoteRequestLineItems: false,
                    };
                },
            );

            return {
                quoteRequests,
                supplierPortalSettings: await supplierPortalSettingsPromise,
            };
        },
        select: ({
            quoteRequests,
            supplierPortalSettings,
        }): {
            quoteRequests: QuoteRequestWithQuoteTracking[];
            defaultQuoteRequest: QuoteRequestWithQuoteTracking | undefined;
            initialFieldConfiguration: FormFieldsConfiguration;
        } => {
            const defaultQuoteRequest = quoteRequests.length > 0 ? quoteRequests[0] : undefined;

            return {
                quoteRequests,
                defaultQuoteRequest,
                initialFieldConfiguration: createFormFieldConfiguration({
                    quoteRequest: defaultQuoteRequest?.quoteRequest,
                    supplierPortalSettings,
                    defaultFormFieldsConfiguration,
                }),
            };
        },
    });
}

function createFormFieldConfiguration({
    quoteRequest,
    supplierPortalSettings,
    defaultFormFieldsConfiguration,
}: {
    quoteRequest?: QuoteRequestDTO;
    supplierPortalSettings?: SupplierPortalSettingsDTO;
    defaultFormFieldsConfiguration: FormFieldsConfiguration;
}): FormFieldsConfiguration {
    const hiddenFields = quoteRequest?.configuration?.hidden_fields ?? [];
    const requiredFields = quoteRequest?.configuration?.required_fields ?? [];

    function getSupplierPortalField(field: QuoteRequestField): SupplierPortalColumnSettings | undefined {
        switch (field) {
            // These columns are always required
            case 'Mpn':
            case 'OfferedMpn':
            case 'Manufacturer':
            case 'OfferedManufacturer':
            case 'UnitPrice':
            case 'UnitPriceCurrency':
                return undefined;
            case 'Ipn':
            case 'Description':
                return supplierPortalSettings?.custom_part_quote_columns?.ipn;
            case 'MinimumOrderQuantity':
                return supplierPortalSettings?.quote_columns?.moq;
            case 'MinimumPackagingQuantity':
                return supplierPortalSettings?.quote_columns?.mpq;
            case 'Packaging':
                return supplierPortalSettings?.quote_columns?.packaging;
            case 'LeadTime':
                return supplierPortalSettings?.quote_columns?.stdFactoryLeadTime;
            case 'LeadTimeUnit':
                return supplierPortalSettings?.quote_columns?.stdFactoryLeadTimeUnit;
            case 'AdditionalNotes':
                return supplierPortalSettings?.quote_columns?.notes;
            case 'Ncnr':
                return supplierPortalSettings?.quote_columns?.ncnr;
            case 'Stock':
                return supplierPortalSettings?.quote_columns?.stock;
            case 'SupplierPartNumber':
                return supplierPortalSettings?.quote_columns?.supplierPartNumber;
            case 'ValidFrom':
                return supplierPortalSettings?.quote_columns?.validFrom;
            case 'ValidUntil':
                return supplierPortalSettings?.quote_columns?.validUntil;
            case 'CancellationWindow':
                return supplierPortalSettings?.quote_columns?.cancellationWindow;
            case 'CancellationTimeUnit':
                return supplierPortalSettings?.quote_columns?.cancellationWindowUnit;
            case 'ItemClass':
                return supplierPortalSettings?.quote_columns?.itemClass;
            case 'OneTimeCost':
                return supplierPortalSettings?.quote_columns?.oneTimeCosts;
            case 'UnitPriceQuantity':
                return supplierPortalSettings?.quote_columns?.pricePer;
            case 'RequiredQuantity':
                return supplierPortalSettings?.quote_columns?.quantity;
            case 'PotentialQuantity':
                return supplierPortalSettings?.quote_columns?.potentialQuantity;
            case 'Bid':
                return supplierPortalSettings?.quote_columns?.bid;
            case 'Unit':
            case 'Attachment':
            case 'TargetPrice':
            case 'Customer':
            case 'NegotiatedByCustomer':
            case 'Recipient':
                return undefined; // These fields don't have corresponding columns in the supplier portal settings
        }
    }

    function getFieldConfiguration(
        field: QuoteRequestField,
        defaultConfig: { visible: boolean; required?: boolean },
    ): FormFieldConfiguration {
        if (requiredFields.includes(field)) {
            return {
                visible: true,
                required: true,
            };
        }

        if (hiddenFields.includes(field)) {
            return {
                visible: false,
                required: false,
            };
        }

        const supplierPortalField = getSupplierPortalField(field);
        if (supplierPortalField?.hidden) {
            return {
                visible: false,
                required: false,
            };
        }

        return {
            visible: defaultConfig.visible,
            required: defaultConfig.required,
        };
    }

    return {
        // Requirements
        ipn: defaultFormFieldsConfiguration.ipn,
        requestedPart: defaultFormFieldsConfiguration.requestedPart,
        partDescription: defaultFormFieldsConfiguration.partDescription,
        requiredQuantity: getFieldConfiguration('RequiredQuantity', defaultFormFieldsConfiguration.requiredQuantity),
        potentialQuantity: getFieldConfiguration('PotentialQuantity', defaultFormFieldsConfiguration.potentialQuantity),
        recipients: getFieldConfiguration('Recipient', defaultFormFieldsConfiguration.recipients),
        targetPrice: getFieldConfiguration('TargetPrice', defaultFormFieldsConfiguration.targetPrice),
        customerName: getFieldConfiguration('Customer', defaultFormFieldsConfiguration.customerName),

        // Offer
        // We don't show the bid field for negotiations as it's not relevant. Every offer is assumed to be a bid.
        bid: defaultFormFieldsConfiguration.bid,
        part: getFieldConfiguration('OfferedMpn', defaultFormFieldsConfiguration.part),
        unitPrice: getFieldConfiguration('UnitPrice', defaultFormFieldsConfiguration.unitPrice),
        moq: getFieldConfiguration('MinimumOrderQuantity', defaultFormFieldsConfiguration.moq),
        mpq: getFieldConfiguration('MinimumPackagingQuantity', defaultFormFieldsConfiguration.mpq),
        currency: getFieldConfiguration('UnitPriceCurrency', defaultFormFieldsConfiguration.currency),
        packaging: getFieldConfiguration('Packaging', defaultFormFieldsConfiguration.packaging),
        standardFactoryLeadTime: getFieldConfiguration(
            'LeadTime',
            defaultFormFieldsConfiguration.standardFactoryLeadTime,
        ),
        standardFactoryLeadTimeUnit: getFieldConfiguration(
            'LeadTimeUnit',
            defaultFormFieldsConfiguration.standardFactoryLeadTimeUnit,
        ),
        notes: getFieldConfiguration('AdditionalNotes', defaultFormFieldsConfiguration.notes),
        ncnr: getFieldConfiguration('Ncnr', defaultFormFieldsConfiguration.ncnr),
        stock: getFieldConfiguration('Stock', defaultFormFieldsConfiguration.stock),
        supplierPartNumber: getFieldConfiguration(
            'SupplierPartNumber',
            defaultFormFieldsConfiguration.supplierPartNumber,
        ),
        validFrom: getFieldConfiguration('ValidFrom', defaultFormFieldsConfiguration.validFrom),
        validUntil: getFieldConfiguration('ValidUntil', defaultFormFieldsConfiguration.validUntil),
        cancellationWindow: getFieldConfiguration(
            'CancellationWindow',
            defaultFormFieldsConfiguration.cancellationWindow,
        ),
        cancellationTimeUnit: getFieldConfiguration(
            'CancellationTimeUnit',
            defaultFormFieldsConfiguration.cancellationTimeUnit,
        ),
        itemClass: getFieldConfiguration('ItemClass', defaultFormFieldsConfiguration.itemClass),
        validFor: defaultFormFieldsConfiguration.validFor,
        oneTimeCost: getFieldConfiguration('OneTimeCost', defaultFormFieldsConfiguration.oneTimeCost),

        // Part information
        reach: defaultFormFieldsConfiguration.reach,
        rohs: defaultFormFieldsConfiguration.rohs,
        eccnNumbers: defaultFormFieldsConfiguration.eccnNumbers,
        htsCode: defaultFormFieldsConfiguration.htsCode,
        countryOfOrigin: defaultFormFieldsConfiguration.countryOfOrigin,
    };
}

function fetchSupplierPortalSettings(
    useSupplierPortalSettings: boolean,
): Promise<SupplierPortalSettingsDTO | undefined> {
    if (!useSupplierPortalSettings) {
        return Promise.resolve(undefined);
    }

    return http('GET /organization-settings/supplier-portal-settings', {}, getToken());
}
